import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Checkbox, IconButton, Tooltip, Typography } from "@mui/material";
import {
  ArrowDownward as MoveDownIcon,
  ArrowUpward as MoveUpIcon,
  DragIndicator as DragHandleIcon,
} from "@mui/icons-material";
import { ColumnId, SheetColumn } from "./sheetTypes";

/**
 * The Columns panel's list: a checkbox per column, in the order the person is
 * looking at, with a handle to drag it somewhere else and arrows for the same
 * move without a pointer.
 *
 * This is the route that works where the header does not - on a touch screen,
 * from the keyboard, and in card layout, which has no column header at all.
 * The gesture is the house pattern (pointer capture plus a ref holding the
 * gesture), but far simpler than the grid's: one column of rows, nothing
 * virtualised, no frozen block and no horizontal scrolling to fight.
 */

interface ColumnOrderListProps {
  /** What to list. Equal to the full order unless a search narrows it. */
  columns: SheetColumn[];
  hiddenColumns: Record<string, true>;
  /**
   * False while a search narrows the list: a drop between two shown rows that
   * have thirty filtered-out rows between them has no honest meaning.
   */
  reorderEnabled: boolean;
  onToggleColumn: (columnId: ColumnId, visible: boolean) => void;
  onMoveColumn: (columnId: ColumnId, beforeColumnId: ColumnId | null) => void;
}

interface DragState {
  columnId: ColumnId;
  fromIndex: number;
  pointerId: number;
  /** Row midpoints in viewport space, read once at the start of the drag. */
  midpoints: number[];
}

const AUTO_SCROLL_EDGE = 24;
const AUTO_SCROLL_STEP = 6;

const ColumnOrderList: React.FC<ColumnOrderListProps> = ({
  columns,
  hiddenColumns,
  reorderEnabled,
  onToggleColumn,
  onMoveColumn,
}) => {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const rowsRef = useRef<Record<string, HTMLDivElement | null>>({});
  const dragRef = useRef<DragState | null>(null);
  const pointerYRef = useRef(0);
  const frameRef = useRef<number | null>(null);
  const [dragColumn, setDragColumn] = useState<ColumnId | null>(null);
  const [dropIndex, setDropIndex] = useState<number | null>(null);

  const stopAutoScroll = useCallback(() => {
    if (frameRef.current != null) {
      window.cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
    }
  }, []);

  useEffect(() => stopAutoScroll, [stopAutoScroll]);

  const insertionAt = useCallback((clientY: number) => {
    const drag = dragRef.current;
    if (!drag) return 0;
    for (let i = 0; i < drag.midpoints.length; i++) {
      if (clientY < drag.midpoints[i]) return i;
    }
    return drag.midpoints.length;
  }, []);

  const autoScrollTick = useCallback(() => {
    frameRef.current = null;
    const box = scrollRef.current;
    if (!dragRef.current || !box) return;
    const rect = box.getBoundingClientRect();
    let step = 0;
    if (pointerYRef.current < rect.top + AUTO_SCROLL_EDGE) {
      step = -AUTO_SCROLL_STEP;
    } else if (pointerYRef.current > rect.bottom - AUTO_SCROLL_EDGE) {
      step = AUTO_SCROLL_STEP;
    }
    if (step === 0) return;
    const before = box.scrollTop;
    box.scrollTop = before + step;
    if (box.scrollTop !== before) {
      // The rows moved under a pointer that did not, so every midpoint the
      // drag started with has shifted by the same amount.
      const drag = dragRef.current;
      const delta = box.scrollTop - before;
      drag.midpoints = drag.midpoints.map((value) => value - delta);
      setDropIndex(insertionAt(pointerYRef.current));
    }
    frameRef.current = window.requestAnimationFrame(autoScrollTick);
  }, [insertionAt]);

  const handleHandleDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>, index: number) => {
      if (!reorderEnabled || columns.length < 2) return;
      if (!event.isPrimary || event.button !== 0) return;
      event.preventDefault();
      const midpoints = columns.map((column) => {
        const node = rowsRef.current[column.id];
        if (!node) return Number.POSITIVE_INFINITY;
        const rect = node.getBoundingClientRect();
        return rect.top + rect.height / 2;
      });
      (event.currentTarget as HTMLDivElement).setPointerCapture?.(
        event.pointerId,
      );
      dragRef.current = {
        columnId: columns[index].id,
        fromIndex: index,
        pointerId: event.pointerId,
        midpoints,
      };
      pointerYRef.current = event.clientY;
      setDragColumn(columns[index].id);
      setDropIndex(index);
    },
    [columns, reorderEnabled],
  );

  const handleHandleMove = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const drag = dragRef.current;
      if (!drag || event.pointerId !== drag.pointerId) return;
      pointerYRef.current = event.clientY;
      setDropIndex(insertionAt(event.clientY));
      if (frameRef.current == null) {
        frameRef.current = window.requestAnimationFrame(autoScrollTick);
      }
    },
    [insertionAt, autoScrollTick],
  );

  const endDrag = useCallback(
    (event: React.PointerEvent<HTMLDivElement>, commit: boolean) => {
      const drag = dragRef.current;
      if (!drag || event.pointerId !== drag.pointerId) return;
      const insertion = dropIndex ?? drag.fromIndex;
      dragRef.current = null;
      stopAutoScroll();
      const node = event.currentTarget as HTMLDivElement;
      if (node.hasPointerCapture?.(event.pointerId)) {
        node.releasePointerCapture(event.pointerId);
      }
      setDragColumn(null);
      setDropIndex(null);
      if (!commit) return;
      // The two insertions that mean "where it already is".
      if (insertion === drag.fromIndex || insertion === drag.fromIndex + 1) {
        return;
      }
      const before = columns[insertion];
      onMoveColumn(drag.columnId, before ? before.id : null);
    },
    [columns, dropIndex, onMoveColumn, stopAutoScroll],
  );

  const moveByOne = useCallback(
    (index: number, delta: number) => {
      const target = index + delta;
      if (target < 0 || target >= columns.length) return;
      const before = delta > 0 ? columns[target + 1] : columns[target];
      onMoveColumn(columns[index].id, before ? before.id : null);
    },
    [columns, onMoveColumn],
  );

  return (
    <Box
      ref={scrollRef}
      sx={{ maxHeight: 280, overflowY: "auto", px: 1.5, py: 0.5 }}
    >
      {columns.map((column, index) => {
        const name = column.label || column.letter;
        return (
          <Box
            key={column.id}
            ref={(node: HTMLDivElement | null) => {
              rowsRef.current[column.id] = node;
            }}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              minWidth: 0,
              borderTop: "2px solid",
              borderTopColor:
                dropIndex === index && dragColumn !== null
                  ? "primary.main"
                  : "transparent",
              borderBottom: "2px solid",
              borderBottomColor:
                dropIndex === columns.length &&
                index === columns.length - 1 &&
                dragColumn !== null
                  ? "primary.main"
                  : "transparent",
              opacity: dragColumn === column.id ? 0.4 : 1,
            }}
          >
            <Tooltip
              title={
                reorderEnabled
                  ? "Drag to move this column"
                  : "Clear the search to reorder columns"
              }
            >
              <Box
                aria-hidden
                onPointerDown={(event: React.PointerEvent<HTMLDivElement>) =>
                  handleHandleDown(event, index)
                }
                onPointerMove={handleHandleMove}
                onPointerUp={(event: React.PointerEvent<HTMLDivElement>) =>
                  endDrag(event, true)
                }
                onPointerCancel={(event: React.PointerEvent<HTMLDivElement>) =>
                  endDrag(event, false)
                }
                sx={{
                  display: "flex",
                  alignItems: "center",
                  color: reorderEnabled ? "text.disabled" : "action.disabled",
                  cursor: reorderEnabled ? "grab" : "default",
                  touchAction: "none",
                }}
              >
                <DragHandleIcon sx={{ fontSize: 18 }} />
              </Box>
            </Tooltip>
            <Checkbox
              size="small"
              checked={!hiddenColumns[column.id]}
              inputProps={{ "aria-label": `Show column ${name}` }}
              onChange={(event) =>
                onToggleColumn(column.id, event.target.checked)
              }
            />
            <Typography
              variant="body2"
              sx={{
                flex: 1,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={`${column.letter} ${column.label}`}
            >
              <Box component="span" sx={{ color: "text.disabled", mr: 0.5 }}>
                {column.letter}
              </Box>
              {column.label}
            </Typography>
            <IconButton
              size="small"
              sx={{ p: 0.25 }}
              disabled={!reorderEnabled || index === 0}
              aria-label={`Move ${name} earlier`}
              title="Move earlier (left in the table)"
              onClick={() => moveByOne(index, -1)}
            >
              <MoveUpIcon sx={{ fontSize: 16 }} />
            </IconButton>
            <IconButton
              size="small"
              sx={{ p: 0.25 }}
              disabled={!reorderEnabled || index === columns.length - 1}
              aria-label={`Move ${name} later`}
              title="Move later (right in the table)"
              onClick={() => moveByOne(index, 1)}
            >
              <MoveDownIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        );
      })}
      {columns.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
          No columns match your search.
        </Typography>
      )}
    </Box>
  );
};

export default ColumnOrderList;
